import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Grid,
  TablePagination,
  InputAdornment
} from '@mui/material';
import { ArrowBack, Delete, Add, Remove, Edit, Visibility, VisibilityOff, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import TitleBar from '../../components/TitleBar/TitleBar';
import { formatDisplayDateTime } from '../../utils/dateUtils';
import {
  addDefaultAuthUser,
  getUsers,
  getRoles,
  addRole,
  revokeRole,
  deleteUser,
  updateUser,
  getRolePermissions,
  getAllowedUserPreferences,
  getAllowedUserAttributes
} from '../../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface UserRole {
  role_id: number;
  role_name: string;
}

interface User {
  id: number;
  user_name: string;
  email?: string;
  display_name?: string;
  external_id?: string;
  upn?: string;
  attributes?: { [key: string]: any };
  preferences?: { [key: string]: string };
  status?: string;
  created_at?: string;
  updated_at?: string;
  last_login_at?: string;
  roles: UserRole[];
}

function emptyPreferencesMap(keys: string[]): Record<string, string> {
  const o: Record<string, string> = {};
  keys.forEach((k) => {
    o[k] = '';
  });
  return o;
}

function mergePreferenceValues(
  keys: string[],
  existing?: { [key: string]: string } | null
): Record<string, string> {
  const base = emptyPreferencesMap(keys);
  if (!existing) return base;
  keys.forEach((k) => {
    if (existing[k] != null && existing[k] !== '') base[k] = String(existing[k]);
  });
  return base;
}

function compactStringMap(m: Record<string, string>): Record<string, string> | undefined {
  const out: Record<string, string> = {};
  Object.entries(m).forEach(([k, v]) => {
    if (v != null && String(v).trim() !== '') out[k] = String(v).trim();
  });
  return Object.keys(out).length ? out : undefined;
}

/** Plain object for attribute chips and update payloads (API may send JSON strings or odd shapes). */
function normalizeAttributesRecord(raw: unknown): Record<string, unknown> {
  if (raw == null || raw === '') return {};
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return { ...(parsed as Record<string, unknown>) };
      }
      return {};
    } catch {
      return {};
    }
  }
  if (raw instanceof Map) {
    return Object.fromEntries(raw);
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return { ...(raw as Record<string, unknown>) };
  }
  return {};
}

interface Role {
  entity_type: string;
  id: number;
  name: string;
  description: string;
}

interface RolePermission {
  role_name: string;
  permissions: string[];
}

interface RolePermissionResponse {
  app_role_id: number;
  role_name: number;
  app_permission_id: string;
  permission_name: string;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const UserManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolePermissions, setRolePermissions] = useState<RolePermission[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedRole, setSelectedRole] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string>('');
  const [openRevokeDialog, setOpenRevokeDialog] = useState(false);
  const [roleToRevoke, setRoleToRevoke] = useState<{
    userId: number;
    roleId: number;
    roleName: string;
  } | null>(null);
  const [userNameError, setUserNameError] = useState('');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editFormData, setEditFormData] = useState({
    user_id: 0,
    user_name: '',
    password: '',
    email: '',
    display_name: '',
    status: '',
    attributes: {} as { [key: string]: any },
    preferences: {} as Record<string, string>
  });
  const [editAttributeKey, setEditAttributeKey] = useState('');
  const [editAttributeValue, setEditAttributeValue] = useState('');
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [addFormData, setAddFormData] = useState({
    user_name: '',
    password: '',
    email: '',
    display_name: '',
    status: 'ACTIVE',
    attributes: {} as { [key: string]: any },
    preferences: {} as Record<string, string>
  });
  const [addAttributeKey, setAddAttributeKey] = useState('');
  const [addAttributeValue, setAddAttributeValue] = useState('');
  const [showAddPassword, setShowAddPassword] = useState(false);

  const [allowedPreferenceKeys, setAllowedPreferenceKeys] = useState<string[]>([]);
  const [preferenceValueOptions, setPreferenceValueOptions] = useState<Record<string, string[]>>({});
  const [allowedAttributeKeys, setAllowedAttributeKeys] = useState<string[]>([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  
  // Validation function to check if add user form is valid
  const isAddFormValid = () => {
    // Check required fields
    if (!addFormData.user_name || !addFormData.password || !addFormData.email || !addFormData.display_name || !addFormData.status) {
      return false;
    }
    
    // Check username format
    if (!validateUserName(addFormData.user_name)) {
      return false;
    }
    
    // Check for duplicates
    const existingUser = users.find(user => 
      user.user_name.toLowerCase() === addFormData.user_name.toLowerCase() ||
      (user.email && user.email.toLowerCase() === addFormData.email.toLowerCase()) ||
      (addFormData.display_name && user.display_name && 
       user.display_name.toLowerCase() === addFormData.display_name.toLowerCase())
    );
    
    return !existingUser;
  };

  const validateUserName = (userName: string) => {
    const validPattern = /^[a-zA-Z0-9._@-]+$/;
    return validPattern.test(userName);
  };

  const handleAddFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'user_name') {
      if (value && !validateUserName(value)) {
        setUserNameError(
          'Username can only contain letters, numbers, and the following special characters: - _ . @'
        );
      } else {
        setUserNameError('');
      }
    }

    setAddFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const filteredUsers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      if (String(u.id).includes(q)) return true;
      const un = (u.user_name || '').toLowerCase();
      const dn = (u.display_name || '').toLowerCase();
      const em = (u.email || '').toLowerCase();
      return un.includes(q) || dn.includes(q) || em.includes(q);
    });
  }, [users, searchQuery]);

  const pagedUsers = useMemo(
    () => filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredUsers, page, rowsPerPage]
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes, permissionsRes, prefRes, attrRes] = await Promise.all([
        getUsers(),
        getRoles(),
        getRolePermissions(),
        getAllowedUserPreferences(),
        getAllowedUserAttributes()
      ]);

      console.log('Users Response:', usersRes);
      console.log('Roles Response:', rolesRes);
      console.log('Permissions Response:', permissionsRes);

      const permissionsData = permissionsRes.data as RolePermissionResponse[];
      const transformedPermissions = permissionsData.reduce((acc, curr) => {
        const roleGroup = acc.find(group => group.role_name === curr.app_permission_id);
        if (roleGroup) {
          roleGroup.permissions.push(curr.permission_name);
        } else {
          acc.push({
            role_name: curr.app_permission_id,
            permissions: [curr.permission_name]
          });
        }
        return acc;
      }, [] as RolePermission[]);

      const usersData = Array.isArray(usersRes.data) ? usersRes.data : [];
      const rolesData = Array.isArray(rolesRes.data) ? rolesRes.data : [];

      setUsers(
        usersData.map((u: User) => ({
          ...u,
          attributes: normalizeAttributesRecord(u.attributes)
        }))
      );
      setRoles(rolesData);
      setRolePermissions(transformedPermissions);

      const prefPayload = prefRes.data as {
        allowed_user_preferences?: string[];
        value_options_by_key?: Record<string, string[]>;
      };
      setAllowedPreferenceKeys(prefPayload.allowed_user_preferences || []);
      setPreferenceValueOptions(prefPayload.value_options_by_key || {});

      const attrPayload = attrRes.data as { allowed_user_attributes?: string[] };
      setAllowedAttributeKeys(attrPayload.allowed_user_attributes || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
      setUsers([]);
      setRoles([]);
      setRolePermissions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!addFormData.user_name || !addFormData.password || !addFormData.email) {
      setError('Please fill in required fields (username, password, email)');
      return;
    }

    if (!validateUserName(addFormData.user_name)) {
      setError('Invalid username format');
      return;
    }

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Check for existing users with same username, email, or display_name
      const existingUser = users.find(user => 
        user.user_name.toLowerCase() === addFormData.user_name.toLowerCase() ||
        (user.email && user.email.toLowerCase() === addFormData.email.toLowerCase()) ||
        (addFormData.display_name && user.display_name && 
         user.display_name.toLowerCase() === addFormData.display_name.toLowerCase())
      );

      if (existingUser) {
        let errorMessage = 'User already exists with ';
        const conflicts = [];
        
        if (existingUser.user_name.toLowerCase() === addFormData.user_name.toLowerCase()) {
          conflicts.push('username');
        }
        if (existingUser.email && existingUser.email.toLowerCase() === addFormData.email.toLowerCase()) {
          conflicts.push('email');
        }
        if (addFormData.display_name && existingUser.display_name && 
            existingUser.display_name.toLowerCase() === addFormData.display_name.toLowerCase()) {
          conflicts.push('display name');
        }
        
        errorMessage += conflicts.join(', ');
        setError(errorMessage);
        setLoading(false);
        return;
      }

      await addDefaultAuthUser({
        user_name: addFormData.user_name,
        password: addFormData.password,
        email: addFormData.email,
        display_name: addFormData.display_name,
        status: addFormData.status,
        attributes: addFormData.attributes,
        preferences: compactStringMap(addFormData.preferences)
      });
      setSuccess('User added successfully');
      setAddFormData({
        user_name: '',
        password: '',
        email: '',
        display_name: '',
        status: 'ACTIVE',
        attributes: {},
        preferences: emptyPreferencesMap(allowedPreferenceKeys)
      });
      setOpenAddDialog(false);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add user');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRole = async () => {
    if (!selectedUser || !selectedRole) return;

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await addRole({ user_id: selectedUser.id, role_id: selectedRole });
      setSuccess('Role added successfully');
      fetchData();
      setOpenDialog(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add role');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeRole = async (userId: number, roleId: number, roleName: string) => {
    setRoleToRevoke({ userId, roleId, roleName });
    setOpenRevokeDialog(true);
  };

  const confirmRevokeRole = async () => {
    if (!roleToRevoke) return;

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await revokeRole({ user_id: roleToRevoke.userId, role_id: roleToRevoke.roleId });
      setSuccess('Role removed successfully');
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove role');
    } finally {
      setLoading(false);
      setOpenRevokeDialog(false);
      setRoleToRevoke(null);
    }
  };

  const handleDeleteUser = async (userName: string) => {
    setUserToDelete(userName);
    setOpenDeleteDialog(true);
  };

  const confirmDeleteUser = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await deleteUser(userToDelete);
      setSuccess('User deleted successfully');
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete user');
    } finally {
      setLoading(false);
      setOpenDeleteDialog(false);
      setUserToDelete('');
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setEditFormData({
      user_id: user.id,
      user_name: user.user_name,
      password: '',
      email: user.email || '',
      display_name: user.display_name || '',
      status: user.status || 'ACTIVE',
      attributes: normalizeAttributesRecord(user.attributes),
      preferences: mergePreferenceValues(allowedPreferenceKeys, user.preferences)
    });
    setEditAttributeKey('');
    setEditAttributeValue('');
    setOpenEditDialog(true);
  };

  const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditAddAttribute = () => {
    if (editAttributeKey && editAttributeValue) {
      setEditFormData((prev) => ({
        ...prev,
        attributes: {
          ...prev.attributes,
          [editAttributeKey]: editAttributeValue
        }
      }));
      setEditAttributeKey('');
      setEditAttributeValue('');
    }
  };

  const handleEditRemoveAttribute = (key: string) => {
    setEditFormData(prev => {
      const newAttributes = { ...prev.attributes };
      delete newAttributes[key];
      return {
        ...prev,
        attributes: newAttributes
      };
    });
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await updateUser({
        user_id: editFormData.user_id,
        user_name: editFormData.user_name,
        email: editFormData.email,
        display_name: editFormData.display_name,
        status: editFormData.status,
        attributes: editFormData.attributes,
        preferences: compactStringMap(editFormData.preferences),
        ...(editFormData.password?.trim() ? { password: editFormData.password } : {})
      });
      setSuccess('User updated successfully');
      fetchData();
      setOpenEditDialog(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleAddFormAddAttribute = () => {
    if (addAttributeKey && addAttributeValue) {
      setAddFormData((prev) => ({
        ...prev,
        attributes: {
          ...prev.attributes,
          [addAttributeKey]: addAttributeValue
        }
      }));
      setAddAttributeKey('');
      setAddAttributeValue('');
    }
  };

  const handleAddFormRemoveAttribute = (key: string) => {
    setAddFormData(prev => {
      const newAttributes = { ...prev.attributes };
      delete newAttributes[key];
      return {
        ...prev,
        attributes: newAttributes
      };
    });
  };

    return (
        <Box sx={{ flexGrow: 1 }}>
  <TitleBar title="" />
  <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton
          aria-label="Back"
          onClick={() => navigate('/home')}
          sx={{
            border: '1px solid #ccc',
            borderRadius: 2,
            p: 1,
            backgroundColor: '#f5f5f5',
            '&:hover': {
              backgroundColor: '#e0e0e0',
            },
            mr: 2,
          }}
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="h4">User Role and Permission Management</Typography>
      </Box>


                    {(error || success) && (
                        <Alert 
                            severity={error ? 'error' : 'success'} 
                            sx={{ mb: 3 }}
                            onClose={() => error ? setError('') : setSuccess('')}
                        >
                            {error || success}
                        </Alert>
                    )}

                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
                            <Tab label="User Management" />
                            <Tab label="Permission Management" />
                        </Tabs>
                    </Box>

                    <TabPanel value={activeTab} index={0}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                            <Typography variant="h6">Users</Typography>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                <TextField
                                    size="small"
                                    placeholder="Search by id, name, email…"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    sx={{ minWidth: 260 }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search fontSize="small" color="action" />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => {
                                    setAddFormData({
                                        user_name: '',
                                        password: '',
                                        email: '',
                                        display_name: '',
                                        status: 'ACTIVE',
                                        attributes: {},
                                        preferences: emptyPreferencesMap(allowedPreferenceKeys)
                                    });
                                    setAddAttributeKey('');
                                    setAddAttributeValue('');
                                    setUserNameError('');
                                    setOpenAddDialog(true);
                                }}
                                disabled={loading}
                            >
                                Add User
                            </Button>
                            </Box>
                        </Box>
                        <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                            <Table sx={{ minWidth: 1100 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Username</TableCell>
                                        <TableCell>Display name</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell sx={{ maxWidth: 220 }}>Tags</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Last Login</TableCell>
                                        <TableCell sx={{ minWidth: 200 }}>Roles</TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{
                                                position: 'sticky',
                                                right: 0,
                                                zIndex: 2,
                                                bgcolor: 'background.paper',
                                                boxShadow: (theme) => `-8px 0 8px -4px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.08)'}`,
                                            }}
                                        >
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pagedUsers.map((user) => (
                                        <TableRow key={user.id} hover>
                                            <TableCell>{user.user_name}</TableCell>
                                            <TableCell>{user.display_name || user.user_name || '-'}</TableCell>
                                            <TableCell>{user.email || '-'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={user.status?.toUpperCase() === 'ACTIVE' ? 'Active' : 'Inactive'}
                                                    color={user.status?.toUpperCase() === 'ACTIVE' ? 'success' : 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 220 }}>
                                                {user.attributes ? (
                                                    <Box sx={{ maxWidth: 200 }}>
                                                        {Object.entries(user.attributes).map(([key, value]) => (
                                                            <Chip
                                                                key={key}
                                                                label={`${value}`}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{ m: 0.25 }}
                                                            />
                                                        ))}
                                                    </Box>
                                                ) : '-'}
                                            </TableCell>
                                            <TableCell>
                                                {user.last_login_at ? (
                                                    formatDisplayDateTime(user.last_login_at)
                                                ) : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    {user.roles.map((role) => (
                                                        <Chip
                                                            key={role.role_id}
                                                            label={role.role_name}
                                                            onDelete={() => handleRevokeRole(user.id, role.role_id, role.role_name)}
                                                            deleteIcon={<Remove />}
                                                        />
                                                    ))}
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setSelectedUser(user);
                                                            setOpenDialog(true);
                                                        }}
                                                    >
                                                        <Add />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{
                                                    position: 'sticky',
                                                    right: 0,
                                                    zIndex: 1,
                                                    bgcolor: 'background.paper',
                                                    boxShadow: (theme) => `-8px 0 8px -4px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.08)'}`,
                                                }}
                                            >
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => handleEditUser(user)}
                                                    sx={{ mr: 1 }}
                                                    aria-label="Edit user"
                                                >
                                                    <Edit />
                                                </IconButton>
                                                <IconButton
                                                    color="error"
                                                    onClick={() => handleDeleteUser(user.user_name)}
                                                    aria-label="Delete user"
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={filteredUsers.length}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                        />
                    </TabPanel>

                    <TabPanel value={activeTab} index={1}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Role</TableCell>
                                        <TableCell>Permissions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rolePermissions.map((rp) => (
                                        <TableRow key={rp.role_name}>
                                            <TableCell>{rp.role_name}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    {rp.permissions.map((permission) => (
                                                        <Chip
                                                            key={permission}
                                                            label={permission}
                                                            variant="outlined"
                                                        />
                                                    ))}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </TabPanel>
                </Paper>
            </Container>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>Add Role</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Role</InputLabel>
                        <Select
                            value={selectedRole}
                            label="Role"
                            onChange={(e) => setSelectedRole(Number(e.target.value))}
                        >
                            {roles.map((role) => (
                                <MenuItem key={role.id} value={role.id}>
                                    {role.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button onClick={handleAddRole} variant="contained" disabled={loading}>
                        Add
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    Confirm User Deletion
                </DialogTitle>
                <DialogContent>
                    <Typography id="delete-dialog-description">
                        Are you sure you want to delete user "{userToDelete}"? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setOpenDeleteDialog(false)}
                        color="primary"
                    >
                        No, Cancel
                    </Button>
                    <Button
                        onClick={confirmDeleteUser}
                        color="error"
                        variant="contained"
                        disabled={loading}
                        autoFocus
                    >
                        Yes, Delete User
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openRevokeDialog}
                onClose={() => setOpenRevokeDialog(false)}
                aria-labelledby="revoke-dialog-title"
                aria-describedby="revoke-dialog-description"
            >
                <DialogTitle id="revoke-dialog-title">
                    Confirm Role Removal
                </DialogTitle>
                <DialogContent>
                    <Typography id="revoke-dialog-description">
                        Are you sure you want to remove the role "{roleToRevoke?.roleName}" from this user? 
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setOpenRevokeDialog(false)}
                        color="primary"
                    >
                        No, Cancel
                    </Button>
                    <Button
                        onClick={confirmRevokeRole}
                        color="error"
                        variant="contained"
                        disabled={loading}
                        autoFocus
                    >
                        Yes, Remove Role
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openEditDialog}
                onClose={() => setOpenEditDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Edit User</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="user_name"
                                label="Username"
                                value={editFormData.user_name}
                                onChange={handleEditFormChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="password"
                                label="Password (leave empty to keep current)"
                                type="password"
                                value={editFormData.password}
                                onChange={handleEditFormChange}
                                helperText="Leave empty to keep current password"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="email"
                                label="Email"
                                type="email"
                                value={editFormData.email}
                                onChange={handleEditFormChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="display_name"
                                label="Display Name"
                                value={editFormData.display_name}
                                onChange={handleEditFormChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    name="status"
                                    value={editFormData.status}
                                    label="Status"
                                    onChange={(e) => setEditFormData(prev => ({ ...prev, status: e.target.value }))}
                                >
                                    <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                                    <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                Preferences
                            </Typography>
                            <Grid container spacing={2}>
                                {allowedPreferenceKeys.map((key) => (
                                    <Grid item xs={12} sm={6} key={`edit-pref-${key}`}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel id={`edit-pref-${key}`}>{key}</InputLabel>
                                            <Select
                                                labelId={`edit-pref-${key}`}
                                                label={key}
                                                value={editFormData.preferences[key] ?? ''}
                                                onChange={(e) =>
                                                    setEditFormData((prev) => ({
                                                        ...prev,
                                                        preferences: {
                                                            ...prev.preferences,
                                                            [key]: e.target.value as string
                                                        }
                                                    }))
                                                }
                                            >
                                                <MenuItem value="">
                                                    <em>Not set</em>
                                                </MenuItem>
                                                {(preferenceValueOptions[key] || []).map((opt) => (
                                                    <MenuItem key={opt} value={opt}>
                                                        {opt}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                        
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                Attributes
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                                {Object.entries(editFormData.attributes).map(([key, value]) => (
                                    <Chip
                                        key={key}
                                        label={`${key}: ${value === null || value === undefined ? '' : String(value)}`}
                                        onDelete={() => handleEditRemoveAttribute(key)}
                                        sx={{ m: 0.5 }}
                                    />
                                ))}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                <FormControl size="small" sx={{ minWidth: 160 }}>
                                    <InputLabel id="edit-attr-key">Attribute</InputLabel>
                                    <Select
                                        labelId="edit-attr-key"
                                        label="Attribute"
                                        value={editAttributeKey}
                                        onChange={(e) => setEditAttributeKey(e.target.value as string)}
                                    >
                                        {allowedAttributeKeys
                                            .filter((k) => editFormData.attributes[k] === undefined)
                                            .map((k) => (
                                                <MenuItem key={k} value={k}>
                                                    {k}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                    size="small"
                                    label="Value"
                                    value={editAttributeValue}
                                    onChange={(e) => setEditAttributeValue(e.target.value)}
                                    sx={{ minWidth: 200 }}
                                />
                                <Button
                                    variant="outlined"
                                    onClick={handleEditAddAttribute}
                                    disabled={!editAttributeKey || !editAttributeValue}
                                >
                                    Add
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
                    <Button 
                        onClick={handleUpdateUser} 
                        variant="contained" 
                        disabled={loading}
                    >
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openAddDialog}
                onClose={() => setOpenAddDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Add New User</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="user_name"
                                label="Username"
                                value={addFormData.user_name}
                                onChange={handleAddFormChange}
                                error={!!userNameError}
                                helperText={userNameError}
                                autoComplete="off"
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="password"
                                label="Password"
                                type={showAddPassword ? 'text' : 'password'}
                                value={addFormData.password}
                                onChange={handleAddFormChange}
                                autoComplete="new-password"
                                required
                                InputProps={{
                                    endAdornment: (
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => setShowAddPassword(!showAddPassword)}
                                            edge="end"
                                        >
                                            {showAddPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="email"
                                label="Email"
                                type="email"
                                value={addFormData.email}
                                onChange={handleAddFormChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="display_name"
                                label="Display Name"
                                value={addFormData.display_name}
                                onChange={handleAddFormChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    name="status"
                                    value={addFormData.status}
                                    label="Status"
                                    onChange={(e) => setAddFormData(prev => ({ ...prev, status: e.target.value }))}
                                >
                                    <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                                    <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                Preferences
                            </Typography>
                            <Grid container spacing={2}>
                                {allowedPreferenceKeys.map((key) => (
                                    <Grid item xs={12} sm={6} key={`add-pref-${key}`}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel id={`add-pref-${key}`}>{key}</InputLabel>
                                            <Select
                                                labelId={`add-pref-${key}`}
                                                label={key}
                                                value={addFormData.preferences[key] ?? ''}
                                                onChange={(e) =>
                                                    setAddFormData((prev) => ({
                                                        ...prev,
                                                        preferences: {
                                                            ...prev.preferences,
                                                            [key]: e.target.value as string
                                                        }
                                                    }))
                                                }
                                            >
                                                <MenuItem value="">
                                                    <em>Not set</em>
                                                </MenuItem>
                                                {(preferenceValueOptions[key] || []).map((opt) => (
                                                    <MenuItem key={opt} value={opt}>
                                                        {opt}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                        
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                Attributes
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                                {Object.entries(addFormData.attributes).map(([key, value]) => (
                                    <Chip
                                        key={key}
                                        label={`${key}: ${value}`}
                                        onDelete={() => handleAddFormRemoveAttribute(key)}
                                        sx={{ m: 0.5 }}
                                    />
                                ))}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                <FormControl size="small" sx={{ minWidth: 160 }}>
                                    <InputLabel id="add-attr-key">Attribute</InputLabel>
                                    <Select
                                        labelId="add-attr-key"
                                        label="Attribute"
                                        value={addAttributeKey}
                                        onChange={(e) => setAddAttributeKey(e.target.value as string)}
                                    >
                                        {allowedAttributeKeys
                                            .filter((k) => addFormData.attributes[k] === undefined)
                                            .map((k) => (
                                                <MenuItem key={k} value={k}>
                                                    {k}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                    size="small"
                                    label="Value"
                                    value={addAttributeValue}
                                    onChange={(e) => setAddAttributeValue(e.target.value)}
                                    sx={{ minWidth: 200 }}
                                />
                                <Button
                                    variant="outlined"
                                    onClick={handleAddFormAddAttribute}
                                    disabled={!addAttributeKey || !addAttributeValue}
                                >
                                    Add
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
                    <Button 
                        onClick={handleAddUser} 
                        variant="contained" 
                        disabled={loading || !!userNameError || !isAddFormValid()}
                    >
                        Add User
                    </Button>
                </DialogActions>
            </Dialog>

            {loading && (
                <Box sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    zIndex: 9999
                }}>
                    <CircularProgress />
                </Box>
            )}
        </Box>
    );
};

export default UserManagement;